import { useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

type SeedResult = components["schemas"]["SeedMatchPracticeDataAdminResponse"];
type SeededLine = components["schemas"]["SeededMatchLineResponse"];
type Scenario = components["schemas"]["MatchPracticeScenario"];
type Lane = components["schemas"]["MatchPracticeLane"];

// The generated schema widens int32 to `number | string`; counts are narrowed once here.
function toNumber(value: number | string | undefined): number {
  const parsed = typeof value === "string" ? Number(value) : value;
  return parsed === undefined || Number.isNaN(parsed) ? 0 : parsed;
}

// In the order they're worth reaching for. Both seed twelve deterministic bills; the choice is what's demonstrated.
const SCENARIOS: { id: Scenario; title: string; blurb: string }[] = [
  {
    id: "ManualSet",
    title: "Nothing matched",
    blurb:
      "Twelve bills with the whole reconciliation left to do. Each needs several purchase orders and " +
      "more delivery notes, with price variances, short and over deliveries, mismatched wording and " +
      "paperwork dated weeks off the invoice.",
  },
  {
    id: "MixedMatchTypes",
    title: "Mixed match types",
    blurb:
      "Twelve bills whose lines match in different ways — matched outright on SKU, matched unattended on " +
      "wording, merely suggested, invisible to the engine, and unmatchable — so one bill shows the whole " +
      "range at once.",
  },
];

const LANE_LABELS: Record<Lane, string> = {
  SkuExact: "SKU",
  FuzzyAuto: "Auto",
  SuggestedManual: "Suggested",
  NoSuggestion: "Not found",
  UnmatchedCharge: "No paperwork",
  Manual: "Manual",
};

interface SeedMatchPracticeDataDialogProps {
  organisationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSeeded: () => void;
}

export function SeedMatchPracticeDataDialog({
  organisationId,
  open,
  onOpenChange,
  onSeeded,
}: SeedMatchPracticeDataDialogProps) {
  const [scenario, setScenario] = useState<Scenario>("ManualSet");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SeedResult | null>(null);

  function resetForm() {
    setScenario("ManualSet");
    setSubmitting(false);
    setError(null);
    setResult(null);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const { data, error: apiError } = await api.POST(
      "/admin/organisations/{organisationId}/match-practice-data",
      {
        params: { path: { organisationId } },
        body: { scenario },
      },
    );

    setSubmitting(false);

    if (apiError || !data) {
      setError("Failed to add practice data.");
      return;
    }

    setResult(data);
    // Refresh behind the dialog so the new bills are already there when it closes.
    onSeeded();
  }

  const notAsIntended = toNumber(result?.linesNotAsIntended);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        {result ? (
          <>
            <DialogHeader>
              <DialogTitle>Practice data added</DialogTitle>
              <DialogDescription>
                {result.billsCreated} bills.{" "}
                {result.scenario === "MixedMatchTypes"
                  ? "Lines match in different ways — open one and look at the mix."
                  : "Nothing is matched — the whole reconciliation is left to do."}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-4 gap-2 text-center text-sm">
              <Stat label="Bills" value={toNumber(result.billsCreated)} />
              <Stat label="Bill lines" value={toNumber(result.billLinesCreated)} />
              <Stat label="Orders" value={toNumber(result.purchaseOrdersCreated)} />
              <Stat label="Deliveries" value={toNumber(result.deliveryNotesCreated)} />
            </div>

            {/* Reports what the engine actually did: lanes land relative to this client's thresholds. */}
            {notAsIntended > 0 && (
              <p className="text-amber-600 text-sm dark:text-amber-400">
                {notAsIntended} line{notAsIntended === 1 ? "" : "s"} did not match the way the scenario
                intended — this client's matching thresholds moved them into a different lane. The lanes
                below show what actually happened.
              </p>
            )}

            {result.scenario === "ManualSet" && toNumber(result.allocationsCreated) > 0 && (
              <p className="text-amber-600 text-sm dark:text-amber-400">
                {toNumber(result.allocationsCreated)} line
                {toNumber(result.allocationsCreated) === 1 ? " was" : "s were"} matched automatically
                — this client's fuzzy auto-accept threshold is low enough that seeded wording cleared it.
              </p>
            )}

            <div className="space-y-3">
              {result.cases.map((seeded) => (
                <div key={seeded.billId} className="rounded-md border p-3 text-sm">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium">{seeded.invoiceNumber}</span>
                    <span className="text-muted-foreground text-xs">{seeded.supplierName}</span>
                  </div>
                  <p className="text-muted-foreground mt-1 text-xs">
                    {seeded.lineCount} lines · {seeded.purchaseOrderNumbers.length} orders ·{" "}
                    {seeded.deliveryNoteNumbers.length} deliveries
                  </p>

                  {seeded.lines.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {seeded.lines.map((line, i) => (
                        <LaneChip key={`${seeded.billId}-${i}`} line={line} />
                      ))}
                    </div>
                  ) : (
                    <ul className="text-muted-foreground mt-2 list-inside list-disc text-xs">
                      {seeded.exercises.map((exercise) => (
                        <li key={exercise}>{exercise}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            <DialogFooter showCloseButton />
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Add three-way match practice data</DialogTitle>
              <DialogDescription>
                Twelve bills, deterministic — the same suppliers, items, wording and prices every time, so
                what you seed here is what someone else sees when you describe it.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-3">
              {SCENARIOS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setScenario(option.id)}
                  className={`w-full rounded-md border p-3 text-left transition ${
                    scenario === option.id ? "border-primary bg-accent" : "hover:bg-accent/50"
                  }`}
                >
                  <div className="text-sm font-medium">{option.title}</div>
                  <p className="text-muted-foreground mt-1 text-xs">{option.blurb}</p>
                </button>
              ))}

              <p className="text-muted-foreground text-xs">
                Adds to what is already here — existing bills, orders and deliveries are never changed or
                removed.
              </p>
              {error && <p className="text-destructive text-sm">{error}</p>}
            </div>

            <DialogFooter className="mt-6">
              <Button type="submit" disabled={submitting}>
                {submitting && <Spinner className="size-4" />}
                Add practice data
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// One line's lane, and whether it landed there.
function LaneChip({ line }: { line: SeededLine }) {
  const intended = line.intended;
  const landed =
    (intended === "SkuExact" && line.outcome === "AutoMatchedOnSku") ||
    (intended === "FuzzyAuto" && line.outcome === "AutoMatchedOnSimilarity") ||
    (intended === "SuggestedManual" && line.outcome === "Suggested") ||
    ((intended === "NoSuggestion" || intended === "UnmatchedCharge") &&
      line.outcome === "NothingOffered") ||
    intended === "Manual";

  return (
    <Badge
      variant={landed ? "secondary" : "destructive"}
      className="text-[10px]"
      title={`${line.description} — ${landed ? "matched as designed" : `landed as ${line.outcome}`}`}
    >
      {LANE_LABELS[intended]}
    </Badge>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border p-2">
      <div className="text-lg font-semibold">{value}</div>
      <div className="text-muted-foreground text-xs">{label}</div>
    </div>
  );
}
